
const LibraryRegistry = {
    carousel: {
        // Local UMD copy (copied at build time by dev/scripts/build.js).
        // Exposes window.EmblaCarousel.
        url: "/assets/scripts/vendor/embla-carousel.umd.js",
    },
    "carousel-autoplay": {
        url: "/assets/scripts/vendor/embla-carousel-autoplay.umd.js",
    },
    popper: {
        url: "https://cdnjs.cloudflare.com/ajax/libs/popper.js/2.11.6/umd/popper.min.js",
    },
    gsap: {
        url: "https://cdn.jsdelivr.net/npm/gsap@3.12.2/dist/gsap.min.js",
    },
    lodash: {
        url: "https://cdn.jsdelivr.net/npm/lodash@4/lodash.min.js",
    },
    anime: {
        url: "https://cdn.jsdelivr.net/npm/animejs@3.2.1/lib/anime.min.js",
    },
    vue: {
        url: "https://cdn.jsdelivr.net/npm/vue@3/dist/vue.global.prod.js",
        alias: "Vue",
    }
};

export function library(name, overrideUrl = null) {
    const lib = LibraryRegistry[name];
    if (!lib && !overrideUrl) {
        throw new Error(`Library "${name}" not found in registry.`);
    }

    return overrideUrl || lib.url;
}


export const ScriptService = {
    done: false,
    preload: {
        done: false,
        sources: [],
        add(srcs) {
            for (const s of srcs) {
                if (!this.sources.includes(s)) this.sources.push(s);
            }
        }
    },
    lazy: {
        done: false,
        sources: [],
        add(srcs) {
            for (const s of srcs) {
                if (!this.sources.includes(s)) this.sources.push(s);
            }
        }
    },
    before(callback) {
        callback();
        return this.wait();
    },
    async wait() {
        try {
            // Already loaded? Resolve immediately.
            if (this.lazy.done) return Promise.resolve();

            // Already waiting on a previous wait() call? Return that same promise
            // so multiple App.preload() invocations don't double-register listeners.
            if (this._waitPromise) return this._waitPromise;

            this._waitPromise = (async () => {
                await this.preloadScripts();

                return new Promise((resolve, reject) => {
                    const events = ['mousedown', 'mousemove', 'touchstart', 'wheel', 'keydown'];

                    // Handler-level guard: even if 2 events fire in the same tick
                    // (e.g. mousedown + mousemove during a click-drag), handler runs once.
                    let fired = false;
                    const handler = (e) => {
                        if (fired) return;
                        fired = true;
                        events.forEach(ev => document.removeEventListener(ev, handler));
                        console.log('[ScriptService] lazy fire by:', e.type);
                        this.loadScripts(this.lazy.sources)
                            .then(() => { this.lazy.done = true; resolve(); })
                            .catch(err => { console.error("Error during script loading:", err); reject(err); });
                    };

                    console.log('[ScriptService] registering listeners');
                    events.forEach(ev =>
                        document.addEventListener(ev, handler, { once: true, passive: true })
                    );
                });
            })();

            // Tap the resolved promise to detect any double-resolution from outside
            this._waitPromise.then(() => console.log('[ScriptService] _waitPromise RESOLVED'));

            return this._waitPromise;
        } catch (err) {
            // Impose loadErrorPolicy[] here
            console.log("Error during preloading:", err);
            // throw err;
        }
    },
    loadScripts(scripts, sequential = true) {
        const loadOperation = sequential ? scripts.reduce((promise, src) => promise.then(() => this.inject(src)), Promise.resolve()) : Promise.all(scripts.map(src => this.inject(src)));
        return loadOperation.then(() => { })
            .catch(error => {
                // Impose loadErrorPolicy[] here
                console.error(`Error loading scripts ${sequential ? "sequentially" : "in parallel"}:`, error);
                throw error;
            });
    },
    preloadScripts() {
        const scripts = this.preload.sources;
        return new Promise((resolve, reject) => {
            this.loadScripts(scripts, true)
                .then(() => {
                    this.preload.done = true;
                    resolve();
                })
                .catch((error) => {
                    console.error("Error during preloading:", error);
                    reject(error);
                });
        });
    },
    inject(src) {
        return new Promise((resolve, reject) => {
            const srcUrl = new URL(src, window.location.href);
            const currentHost = window.location.hostname;
            const finalSrc = srcUrl.hostname == currentHost
                ? `${src}?v=${App.version.get()}`
                : `${src}`;

            // Dedupe: skip if a <script> with this src is already in the document.
            // We test both the un-versioned src and the resolved absolute URL so
            // we catch same-origin scripts whose cache-bust ?v= may differ.
            const already = document.querySelector(
                `script[src="${finalSrc}"], script[src="${srcUrl.href}"]`
            );
            if (already) {
                // Already loaded (or loading). Resolve immediately so chained
                // .then() callers don't hang waiting for an event that won't fire.
                return resolve(already);
            }

            const script = document.createElement('script');
            script.src = finalSrc;
            script.onload = () => resolve(script);
            script.onerror = () => reject(new Error(`Error (script): ${src}`));
            document.head.appendChild(script);
        });
    }
};


